import { AxiosError } from 'axios';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Column, Row } from 'react-table';
import { backendErrorNotification } from '../../../../components/Notification';
import TablePaginator from '../../../../components/TablePaginator';
import {
  ActionButtonsContainer,
  DeleteIconTable,
  EditIconTable,
} from '../../../../components/TablePaginator/styles';
import HttpStatus from '../../../../enums/httpStatus';
import { mobileOrderStatusApi } from '../../../../services/requests';
import * as TP from '../../../../types/apiResponseTypes';
import Loader from './Loader';

import NewRegisterBtn from '../../../../components/Buttons/NewRegisterBtn';
import NoData from '../../../../components/NoData';
import CreateOrderStatus from './Components/CreateOrderStatus';
import DeleteOrderStatus from './Components/DeleteOrderStatus';
import EditOrderStatus from './Components/EditOrderStatus';

const OrderStatusList = () => {
  const { t } = useTranslation();

  const [waitingGetOrderStatus, setWaitingGetOrderStatus] = useState(false);
  const [orderStatuses, setOrderStatuses] = useState<TP.MobileOrderStatus[]>([]);
  const [orderStatusSelected, setOrderStatusSelected] = useState<TP.MobileOrderStatus | null>(null);

  const [showModalCreate, setShowModalCreate] = useState(false);
  const [showModalEdit, setShowModalEdit] = useState(false);
  const [showModalDelete, setShowModalDelete] = useState(false);

  const reqGetOrderStatuses = useCallback(async () => {
    setWaitingGetOrderStatus(true);

    const source = mobileOrderStatusApi.axios.CancelToken.source();

    try {
      const res = await mobileOrderStatusApi.index(source.token);

      if (res.status === HttpStatus.OK) {
        const { data }: { data: TP.MobileOrderStatus[] } = res.data;
        if (data.length >= 1) {
          setOrderStatuses(data);
        } else {
          setOrderStatuses([]);
        }
      } else {
        setOrderStatuses([]);
        throw res;
      }

      setWaitingGetOrderStatus(false);
    } catch (err) {
      if (!mobileOrderStatusApi.axios.isCancel(err)) {
        backendErrorNotification(err as AxiosError<any, any>);
      }
      setWaitingGetOrderStatus(false);
    }

    return () => {
      source.cancel('Component got unmounted');
    };
  }, []);

  useEffect(() => {
    reqGetOrderStatuses();
  }, []);

  const handleTableEditBtn = (row: Row<TP.MobileOrderStatus>) => {
    setOrderStatusSelected(row.original);
    setShowModalEdit(true);
  };

  const handleTableDeleteBtn = (row: Row<TP.MobileOrderStatus>) => {
    setOrderStatusSelected(row.original);
    setShowModalDelete(true);
  };

  const actionsButtons = (row: Row<TP.MobileOrderStatus>) => (
    <ActionButtonsContainer>
      <EditIconTable onClick={() => handleTableEditBtn(row)} />
      {!row.original.fixed && (
        <DeleteIconTable onClick={() => handleTableDeleteBtn(row)} />
      )}
    </ActionButtonsContainer>
  );

  const columns: Column<TP.MobileOrderStatus>[] = useMemo(
    () => [
      {
        Header: t('sigla') as string,
        accessor: 'sigla',
        Cell: ({ value }: { value: string | null }) => value || '-',
      },
      {
        Header: t('description') as string,
        accessor: 'descricao',
      },
      {
        Header: t('isFinalStatus') as string,
        accessor: 'is_ultimo',
        Cell: ({ value }: { value: 's' | 'n' | null }) =>
          value === 's' ? t('yes') : value === 'n' ? t('no') : '-',
      },
      {
        Header: t('showInOrderList') as string,
        accessor: 'exibe_lista_pedido',
        Cell: ({ value }: { value: 's' | 'n' | null }) =>
          value === 's' ? t('yes') : value === 'n' ? t('no') : '-',
      },
      {
        Header: t('statusColor') as string,
        accessor: 'cor',
        Cell: ({ value }: { value: string | null }) => value || '-',
      },
      {
        Header: t('statusLegend') as string,
        accessor: 'legenda',
        Cell: ({ value }: { value: string | null }) => value || '-',
      },
      {
        Header: t('weight') as string,
        accessor: 'peso',
        Cell: ({ value }: { value: number | null }) => value ?? 0,
      },
      {
        Header: t('actions') as string,
        Cell: ({ row }: any) => actionsButtons(row),
      },
    ],
    [t],
  );

  return (
    <>
      <NewRegisterBtn
        onClick={() => setShowModalCreate(true)}
        disabled={waitingGetOrderStatus}
        style={{ marginLeft: '0', marginBottom: '1rem' }}
      />

      {showModalCreate && (
        <CreateOrderStatus
          closeModal={() => setShowModalCreate(false)}
          reqGetAllOrderStatuses={() => reqGetOrderStatuses()}
        />
      )}

      {showModalDelete && orderStatusSelected && (
        <DeleteOrderStatus
          closeModal={() => setShowModalDelete(false)}
          reqGetAllOrderStatuses={() => reqGetOrderStatuses()}
          orderStatusSelected={orderStatusSelected}
        />
      )}

      {showModalEdit && orderStatusSelected && (
        <EditOrderStatus
          closeModal={() => setShowModalEdit(false)}
          reqGetAllOrderStatuses={() => reqGetOrderStatuses()}
          orderStatusSelected={orderStatusSelected}
        />
      )}

      <Loader isLoading={waitingGetOrderStatus}>
        {orderStatuses.length >= 1 ? (
          <TablePaginator data={orderStatuses} columns={columns} globalFiltering />
        ) : (
          <NoData />
        )}
      </Loader>
    </>
  );
};

export default OrderStatusList;
